import { teacherMessage } from './teacher-language.js';
/** Teacher-only curriculum review. Content never enters browser storage or the student frame here. */
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { BuildResult } from './api.js';
import type { Tier } from '../shared/index.js';
import type { ContentOrigin, FlipCardContent } from '../shared/class-materials.js';
import { MATERIAL_CONTENT_LIMITS, parseFlipCardContent } from '../shared/class-materials.js';

export type MaterialsReviewMode = 'list' | 'gameplay';
export type MaterialsInput = { text: string } | { file: { name: string; mimeType: string; dataBase64: string } };
export interface MaterialsDraftView {
  id: string;
  revision: number;
  content: FlipCardContent;
  build: BuildResult | null;
  approved: boolean;
  gameChecksPassed: boolean;
  gameCheckFailure?: string;
  sourceName?: string;
  warnings?: string[];
  units?: { id: string; title: string }[];
}
export interface MaterialsClient {
  createMaterials(input: MaterialsInput, tier: Tier): Promise<MaterialsDraftView>;
  getMaterials(id: string): Promise<MaterialsDraftView>;
  updateMaterials(id: string, expectedRevision: number, content: FlipCardContent): Promise<MaterialsDraftView>;
  compileMaterials(id: string, expectedRevision: number): Promise<MaterialsDraftView>;
  approveMaterials(id: string, expectedRevision: number, mode: MaterialsReviewMode): Promise<MaterialsDraftView>;
  selectMaterialsUnit(id: string, expectedRevision: number, unitId: string): Promise<MaterialsDraftView>;
}
export interface MaterialsPreviewProps {
  build: BuildResult;
  disabled: boolean;
  /** Runtime-observed IDs only. A generic clean boot must never imply a reviewed deck. */
  onCoverage(cardIds: string[]): void;
  onReady(ready: boolean): void;
}
export interface MaterialsSurfaceProps {
  api: MaterialsClient;
  tier: Tier;
  initialDraftId?: string | undefined;
  onLeave(): void;
  onApproved(build: BuildResult): void;
  onRemember?(draftId?: string): void;
  onBusyChange?: ((busy: boolean) => void) | undefined;
  renderPreview(props: MaterialsPreviewProps): ComponentChildren;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT = 24_000;
const MIN_CARDS = MATERIAL_CONTENT_LIMITS.minCards;
const MAX_CARDS = MATERIAL_CONTENT_LIMITS.maxCards;
const message = (error: unknown) => teacherMessage(error, 'TIP could not confirm this step. Keep any unsaved edits, then reopen the draft to check its status before trying again. If it keeps happening, use Report an issue.');

/** Bounded before FileReader allocates the encoded payload; server independently validates. */
export async function readMaterialFile(file: File): Promise<MaterialsInput> {
  if (!/\.(pdf|docx)$/i.test(file.name)) throw new Error('Choose a PDF or Word document (.docx), or paste its text below.');
  if (file.size === 0) throw new Error('This file is empty. Choose a document with class material.');
  if (file.size > MAX_FILE_BYTES) throw new Error('Choose a file smaller than 5 MB, or paste the unit you want to use.');
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string'
      ? resolve(reader.result.slice(reader.result.indexOf(',') + 1))
      : reject(new Error('This file could not be read. Choose it again.'));
    reader.onerror = () => reject(new Error('This file could not be read. Choose it again.'));
    reader.onabort = () => reject(new Error('File reading stopped. Choose it again.'));
    reader.readAsDataURL(file);
  });
  return { file: { name: file.name, mimeType: file.type, dataBase64: base64 } };
}

function Origin({ origin, field }: { origin: ContentOrigin; field: 'term' | 'answer' }) {
  if (origin.kind === 'source') return <details class="material-origin">
    <summary>From material <span aria-hidden="true">↗</span></summary>
    <p class="material-location">{origin.location}</p>
    <blockquote>{origin.quote}</blockquote>
  </details>;
  return <p class={`material-origin ${origin.kind === 'ai_suggested' ? 'suggested' : ''}`}>
    {origin.kind === 'ai_suggested' ? `AI suggested · check this ${field}` : 'Edited by you'}
  </p>;
}

export function MaterialsSurface(props: MaterialsSurfaceProps) {
  const { api } = props;
  const [inputMode, setInputMode] = useState<'upload' | 'paste'>('upload');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [draft, setDraft] = useState<MaterialsDraftView | null>(null);
  const [content, setContent] = useState<FlipCardContent | null>(null);
  const [mode, setMode] = useState<MaterialsReviewMode>('list');
  const [busy, setBusy] = useState<string | null>(props.initialDraftId ? 'Opening your draft…' : null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [seen, setSeen] = useState<string[]>([]);
  const [previewReady, setPreviewReady] = useState(false);
  const [retryLoad, setRetryLoad] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const [unitId, setUnitId] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const filePicker = useRef<HTMLInputElement>(null);
  const active = useRef(true);
  const request = useRef(0);
  const dirty = !!draft && !!content && JSON.stringify(content) !== JSON.stringify(draft.content);
  const compiled = !!draft?.build && draft.build.status === 'passed' && draft.gameChecksPassed === true && !dirty;
  const currentKey = draft ? `${draft.id}:${draft.revision}` : '';
  const keyRef = useRef(currentKey);
  keyRef.current = currentKey;

  useEffect(() => () => { active.current = false; request.current++; }, []);
  useEffect(() => { props.onBusyChange?.(!!busy); }, [busy, props.onBusyChange]);
  useEffect(() => () => props.onBusyChange?.(false), [props.onBusyChange]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const adopt = (next: MaterialsDraftView) => {
    setDraft(next); setContent(structuredClone(next.content));
    setConfirmed(false); setSeen([]); setPreviewReady(false); setLeaving(false);
    props.onRemember?.(next.id);
  };

  useEffect(() => {
    if (!props.initialDraftId || props.initialDraftId === draft?.id) return;
    const turn = ++request.current;
    setBusy('Opening your draft…'); setError(null);
    api.getMaterials(props.initialDraftId).then(next => {
      if (active.current && turn === request.current) adopt(next);
    }).catch(err => {
      if (active.current && turn === request.current) setError(message(err));
    }).finally(() => {
      if (active.current && turn === request.current) setBusy(null);
    });
  }, [api, props.initialDraftId, retryLoad]);

  useEffect(() => { heading.current?.focus(); }, [draft?.id]);

  const perform = async (label: string, operation: () => Promise<MaterialsDraftView>, success?: string) => {
    if (busy) return;
    const turn = ++request.current;
    setBusy(label); setError(null); setNotice(null);
    try {
      const next = await operation();
      if (!active.current || turn !== request.current) return;
      adopt(next);
      if (success) setNotice(success);
    } catch (err) {
      if (active.current && turn === request.current) setError(message(err));
    } finally {
      if (active.current && turn === request.current) setBusy(null);
    }
  };

  const generate = () => perform('Finding terms and drafting answers…', async () => {
    const input = inputMode === 'upload'
      ? file ? await readMaterialFile(file) : (() => { throw new Error('Choose a document first.'); })()
      : { text: text.trim() };
    if ('text' in input && (!input.text || input.text.length > MAX_TEXT)) throw new Error('Paste between 1 and 24,000 characters of class material.');
    return api.createMaterials(input, props.tier);
  });

  const change = (next: FlipCardContent) => {
    setContent(next); setConfirmed(false); setSeen([]); setPreviewReady(false); setNotice(null); setError(null);
  };
  const editCard = (index: number, field: 'term' | 'answer', value: string) => {
    if (!content) return;
    change({ ...content, cards: content.cards.map((card, at) => at !== index ? card : {
      ...card, [field]: value, [field === 'term' ? 'termOrigin' : 'answerOrigin']: { kind: 'teacher_edited' },
    }) });
  };
  const save = () => draft && content && perform('Saving your changes…', () => api.updateMaterials(draft.id, draft.revision, parseFlipCardContent(content)), 'Changes saved. Check this version before approving it.');
  const prepare = () => draft && perform('Checking your game…', () => api.compileMaterials(draft.id, draft.revision));
  const approve = () => draft && confirmed && compiled && perform('Recording your approval…', async () => {
    const next = await api.approveMaterials(draft.id, draft.revision, mode);
    return next;
  }, 'This version is approved. Choose a class when you are ready to publish.');
  const switchMode = (next: MaterialsReviewMode) => { setMode(next); setConfirmed(false); };
  const suggested = content?.cards.filter(card => card.termOrigin.kind === 'ai_suggested' || card.answerOrigin.kind === 'ai_suggested').length ?? 0;
  const allSeen = !!content && content.cards.every(card => seen.includes(card.id));

  return <section class="materials-surface" aria-labelledby="materials-title">
    <header class="materials-head">
      <div><p class="eyebrow">Flip Card Drill · private draft</p>
        <h2 id="materials-title" ref={heading} tabIndex={-1}>{draft ? 'Review your class content' : 'Use my class materials'}</h2>
      </div>
      <button type="button" class="quiet" disabled={!!busy} onClick={() => dirty ? setLeaving(true) : props.onLeave()}>← Back to the activity</button>
    </header>
    {leaving ? <div class="materials-leave" role="alert">
      <p>Your latest edits have not been saved.</p>
      <button type="button" class="step" onClick={() => void save()}>Save changes</button>
      <button type="button" class="quiet" onClick={() => setLeaving(false)}>Keep editing</button>
      <button type="button" class="quiet" onClick={props.onLeave}>Discard unsaved edits</button>
    </div> : null}
    {error ? <div class="materials-error" role="alert"><p>{error}</p>
      {props.initialDraftId && !draft ? <button class="step" type="button" onClick={() => setRetryLoad(n => n + 1)}>Try opening again</button> : null}
    </div> : null}
    <div role="status" aria-live="polite" class="materials-status">
      {busy ? <p>{busy} Keep this page open.</p> : notice ? <p>{notice}</p> : null}
    </div>
    {!draft ? <div class="materials-input">
      <p class="materials-intro">Bring a worksheet, assignment, or unit outline. TIP drafts the terms and answers, then you review them before students see anything.</p>
      <p class="materials-help">Use lesson materials without student names, grades, or other personal information.</p>
      <fieldset class="materials-modes" disabled={!!busy}>
        <legend>Add class material</legend>
        <label><input type="radio" name="materials-input" checked={inputMode === 'upload'} onChange={() => { setInputMode('upload'); setError(null); }} />Upload a document</label>
        <label><input type="radio" name="materials-input" checked={inputMode === 'paste'} onChange={() => { setInputMode('paste'); setError(null); }} />Paste text</label>
      </fieldset>
      {inputMode === 'upload' ? <div class="materials-upload">
        <input ref={filePicker} id="material-file" class="materials-file" type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" disabled={!!busy} aria-label="Choose class material" aria-describedby="material-file-help" onChange={event => {
          setFile(event.currentTarget.files?.[0] ?? null); setError(null);
        }} />
        <button type="button" class="step" disabled={!!busy} onClick={() => filePicker.current?.click()}>{file ? 'Choose a different file' : 'Upload from computer'}</button>
        {file ? <p class="materials-file-name">{file.name} <span>({(file.size / 1024 / 1024).toFixed(1)} MB)</span></p> : null}
        <p id="material-file-help" class="materials-help">PDF or Word document (.docx), up to 5 MB in size. PDFs can have up to 40 pages. You must be able to select and copy text in the document. For scanned pages, paste the text instead.</p>
      </div> : <div>
        <label class="compose-label" for="material-text">Class material</label>
        <textarea id="material-text" class="field" rows={9} value={text} disabled={!!busy} aria-describedby="material-text-help" onInput={event => setText(event.currentTarget.value)} placeholder="Paste the assignment or the part of your unit you want students to practice." />
        <p id="material-text-help" class="materials-help" role={text.length > MAX_TEXT ? 'alert' : undefined}>{text.length.toLocaleString()} of {MAX_TEXT.toLocaleString()} characters. {text.length > MAX_TEXT ? 'Use a shorter section of your material to continue.' : 'For a syllabus, use the unit you want to teach.'}</p>
      </div>}
      <p class="materials-help">If your material names a topic without explaining it, TIP labels its proposed answer “AI suggested” for you to check.</p>
      <button type="button" class="act" disabled={!!busy || (inputMode === 'upload' ? !file : !text.trim() || text.length > MAX_TEXT)} onClick={() => void generate()}>Create my draft</button>
    </div> : content ? <>
      <p class="materials-intro">Review the terms and answers, or play the game. Both use this same draft. Approval is your decision; TIP checks the game separately.</p>
      <div class="materials-source-row">{draft.sourceName ? <p class="materials-source">Material: {draft.sourceName}</p> : null}
      <button type="button" class="quiet materials-replace" disabled={!!busy || dirty} onClick={() => {
        setDraft(null); setContent(null); setText(''); setFile(null); setConfirmed(false); setSeen([]); setPreviewReady(false); setError(null); setNotice(null); setUnitId(''); props.onRemember?.();
      }}>Use different material</button>
      </div>
      {draft.warnings?.length ? <ul class="materials-warnings">{draft.warnings.map(warning => <li key={warning}>{teacherMessage(warning, 'Please review your material for personal information before continuing.')}</li>)}</ul> : null}
      {draft.units && draft.units.length > 1 ? <details class="materials-unit">
        <summary>Choose a unit from your material ({draft.units.length})</summary>
        <label class="compose-label" for="material-unit">Focus on a unit</label>
        <select id="material-unit" class="field" value={unitId} disabled={!!busy || dirty} onChange={event => setUnitId(event.currentTarget.value)}>
          <option value="">Choose a unit TIP found…</option>
          {draft.units.map(unit => <option key={unit.id} value={unit.id}>{unit.title}</option>)}
        </select>
        <button class="step" type="button" disabled={!!busy || dirty || !unitId} onClick={() => void perform('Drafting cards for this unit…', () => api.selectMaterialsUnit(draft.id, draft.revision, unitId))}>Draft this unit</button>
        <p class="materials-help">This replaces the current draft cards. Save any edits before choosing a unit.</p>
      </details> : null}
      <label class="materials-title-field" for="material-title">Activity title
        <input id="material-title" class="field" maxLength={100} value={content.title} disabled={!!busy} onInput={event => change({ ...content, title: event.currentTarget.value })} />
      </label>
      <fieldset class="materials-modes" disabled={!!busy}>
        <legend>Choose how to review</legend>
        <label><input type="radio" name="materials-review" checked={mode === 'list'} onChange={() => switchMode('list')} />Review terms and answers</label>
        <label><input type="radio" name="materials-review" checked={mode === 'gameplay'} onChange={() => switchMode('gameplay')} />Play and review</label>
      </fieldset>
      <div class="materials-deck-summary"><span>{content.cards.length} cards</span><span>{suggested ? `${suggested} ${suggested === 1 ? 'card has' : 'cards have'} AI suggestions to check` : 'No answers labelled AI suggested'}</span>
        <span>{dirty ? 'Unsaved changes' : `Version ${draft.revision} saved`}</span></div>
      {draft.gameCheckFailure ? <p class="materials-error" role="alert">{teacherMessage(draft.gameCheckFailure, 'This version needs another game check. Choose Check this version to try again.')}</p> : null}
      {draft.build?.status === 'failed' ? <p class="materials-error" role="alert">{teacherMessage(draft.build.failureReason, 'This version did not pass the game checks. Review the content, then save and check it again.')}</p> : null}
      {mode === 'gameplay' ? <section class="materials-play" aria-label="Play this draft">
        {compiled && draft.build ? <>
          {props.renderPreview({ build: draft.build, disabled: !!busy, onCoverage: cardIds => {
            if (keyRef.current !== currentKey) return;
            setSeen(previous => [...new Set([...previous, ...cardIds.filter(id => content.cards.some(card => card.id === id))])]);
          }, onReady: ready => { if (keyRef.current === currentKey) setPreviewReady(ready); } })}
          <p class="materials-coverage" role="status">{seen.length ? `${seen.length} of ${content.cards.length} cards seen in this preview. ` : 'TIP does not keep track of which cards you saw in this preview. '}{allSeen ? 'All cards have appeared.' : 'Playing may not show every card. You can switch to the list to review them all.'}</p>
        </> : <p class="materials-help">{dirty ? 'Save your changes and check the game to play this version.' : 'Check this version to prepare your private game preview.'}</p>}
        <details class="materials-game-edits"><summary>Edit terms and answers</summary><CardEditor content={content} disabled={!!busy} editCard={editCard} change={change} /></details>
      </section> : <CardEditor content={content} disabled={!!busy} editCard={editCard} change={change} />}
      <div class="materials-save-row">
        {dirty ? <button type="button" class="step" disabled={!!busy} onClick={() => void save()}>Save changes</button> : null}
        {!compiled && !dirty ? <button type="button" class="step" disabled={!!busy} onClick={() => void prepare()}>Check this version</button> : null}
        <p class="materials-help">{dirty ? 'Edits need a fresh game check and approval.' : compiled ? 'This version passed the automatic game checks.' : 'TIP checks scoring, layout, and the game before approval.'}</p>
      </div>
      {draft.approved && compiled ? <div class="materials-approval">
        <p class="record-voice"><span class="dot seal" />Version {draft.revision} is approved. It is still private.</p>
        <button type="button" class="act" disabled={!!busy} onClick={() => draft.build && props.onApproved(draft.build)}>Choose a class to publish</button>
      </div> : <div class="materials-approval">
        <label class="materials-attest"><input type="checkbox" checked={confirmed} disabled={!!busy || !compiled || (mode === 'gameplay' && !previewReady)} onChange={event => setConfirmed(event.currentTarget.checked)} />
          <span>I reviewed this version, including any AI suggestions, and its terms and answers are ready for my class.</span>
        </label>
        <button type="button" class="act" disabled={!!busy || !compiled || !confirmed || (mode === 'gameplay' && !previewReady)} onClick={() => void approve()}>Approve this version</button>
        <p class="materials-help">Approving means the content is ready. Students cannot see it until you publish it. Publishing creates a new activity and assignment.</p>
      </div>}
    </> : null}
  </section>;
}

function CardEditor({ content, disabled, editCard, change }: {
  content: FlipCardContent; disabled: boolean;
  editCard(index: number, field: 'term' | 'answer', value: string): void;
  change(content: FlipCardContent): void;
}) {
  return <>
    <ol class="materials-cards">{content.cards.map((card, index) => <li key={card.id} class="materials-card">
      <div class="materials-card-top"><span>Card {index + 1}</span><button type="button" class="quiet" disabled={disabled || content.cards.length <= MIN_CARDS} aria-label={`Remove card ${index + 1}`} onClick={() => change({ ...content, cards: content.cards.filter((_, at) => at !== index) })}>Remove</button></div>
      <div class="materials-card-fields">
        <div><label for={`term-${card.id}`}>Term</label><ContentField id={`term-${card.id}`} value={card.term} maxLength={120} disabled={disabled} onInput={value => editCard(index, 'term', value)} /><Origin origin={card.termOrigin} field="term" /></div>
        <div><label for={`answer-${card.id}`}>Answer or explanation</label><ContentField id={`answer-${card.id}`} value={card.answer} maxLength={500} disabled={disabled} onInput={value => editCard(index, 'answer', value)} /><Origin origin={card.answerOrigin} field="answer" /></div>
      </div>
    </li>)}</ol>
    <div class="materials-add"><button type="button" class="step" disabled={disabled || content.cards.length >= MAX_CARDS} onClick={() => change({ ...content, cards: [...content.cards, {
      id: `teacher-${crypto.randomUUID()}`, term: '', answer: '', termOrigin: { kind: 'teacher_edited' }, answerOrigin: { kind: 'teacher_edited' },
    }] })}>Add a card</button><span class="materials-help">Use {MIN_CARDS}–{MAX_CARDS} cards, each with a different term and answer.</span></div>
  </>;
}

/** Keep every proposed word visible, including when a Chromebook window gets narrower. */
function ContentField(props: { id: string; value: string; maxLength: number; disabled: boolean; onInput(value: string): void }) {
  const field = useRef<HTMLTextAreaElement>(null);
  const resize = () => {
    const node = field.current;
    if (!node) return;
    node.style.height = 'auto';
    if (node.scrollHeight > 0) node.style.height = `${node.scrollHeight + 2}px`;
  };
  useLayoutEffect(resize, [props.value]);
  useEffect(() => {
    const node = field.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    let width = node.clientWidth;
    const observer = new ResizeObserver(() => {
      if (node.clientWidth !== width) { width = node.clientWidth; resize(); }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <textarea ref={field} id={props.id} class="field" rows={2} maxLength={props.maxLength} value={props.value} disabled={props.disabled} onInput={event => props.onInput(event.currentTarget.value)} />;
}
